// src/contexts/Auth0Provider.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import {
  Auth0Client,
  PopupLoginOptions,
  RedirectLoginOptions,
  User,
} from "@auth0/auth0-spa-js";
import { auth0Config } from "../config/Auth0Config";
import { initializeSession } from "../utils/apiUtils";
import { parseJwt } from "../utils/authUtils";

interface AuthContextType {
  isAuthenticated: boolean;
  isInitializing: boolean;
  user: User | null;
  login: (opts?: RedirectLoginOptions | PopupLoginOptions) => Promise<void>;
  logout: () => void;
  getToken: () => Promise<string>;
  getUserEmail: () => string;
  checkTokenValidity: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isInitializing: true,
  user: null,
  login: async () => {},
  logout: () => {},
  getToken: async () => {
    throw new Error("Not initialized");
  },
  getUserEmail: () => "unknown@example.com",
  checkTokenValidity: async () => false,
});

interface AuthProviderProps {
  children: ReactNode;
}

// Create Auth0 client once outside component
export const auth0Client = new Auth0Client(auth0Config);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  // Check if token is valid and not expired
  const checkTokenValidity = useCallback(async (): Promise<boolean> => {
    try {
      // First check if Auth0 client thinks we're authenticated
      const isAuth = await auth0Client.isAuthenticated();
      if (!isAuth) {
        return false;
      }

      // Try to get a token silently - this will fail if refresh token is missing/expired
      try {
        const token = await auth0Client.getTokenSilently({
          authorizationParams: {
            audience: auth0Config.audience,
            scope: auth0Config.scope,
          },
          cacheMode: "off", // Force fresh token check
        });

        if (!token) {
          return false;
        }

        // Parse the token to check expiry
        const tokenPayload = parseJwt(token);
        if (!tokenPayload) {
          return false;
        }

        const now = Math.floor(Date.now() / 1000);
        const tokenExpiry = tokenPayload.exp;

        // Check if token expires within the next 5 minutes (300 seconds)
        const isTokenExpiringSoon = tokenExpiry - now < 300;

        if (isTokenExpiringSoon) {
          return false;
        }

        return true;
      } catch (tokenError: any) {
        // Handle specific refresh token errors
        if (
          tokenError.error === "missing_refresh_token" ||
          tokenError.message?.includes("Missing Refresh Token")
        ) {
          await handleRefreshTokenFailure();
          return false;
        }

        return false;
      }
    } catch (error: any) {
      console.error("Error checking token validity:", error);
      return false;
    }
  }, []);

  // Handle refresh token failure by clearing cache and updating state
  const handleRefreshTokenFailure = useCallback(async () => {
    try {
      // Update state
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error("Error during refresh token failure handling:", error);
    }
  }, []);

  // Handle redirect callback
  const handleRedirect = useCallback(async () => {
    // this gets the code and state from the url that auth0 redirects to to ask auth0 for the token
    // Auth0 client stores the token securely
    const qp = new URLSearchParams(window.location.search);
    if (qp.has("code") && qp.has("state")) {
      try {
        await auth0Client.handleRedirectCallback();
        // Clean URL so the user doesn't see the code and state in the url from auth0
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } catch (error: any) {
        if (error.error !== "login_required") {
          console.error("Error handling redirect callback:", error);
        }
      }
    }
  }, []);

  // Check authentication status with enhanced validation
  const checkAuth = useCallback(async () => {
    try {
      const loggedIn = await auth0Client.isAuthenticated();

      if (loggedIn) {
        // Validate that we can actually get a token
        const tokenValid = await checkTokenValidity();

        if (tokenValid) {
          const profile = await auth0Client.getUser();
          setUser(profile ?? null);
          setIsAuthenticated(true);
        } else {
          await handleRefreshTokenFailure();
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error: any) {
      if (error.error !== "login_required") {
        console.error("Error checking authentication:", error);
      }
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [checkTokenValidity, handleRefreshTokenFailure]);

  // Initialize Auth0
  useEffect(() => {
    const initAuth0 = async () => {
      try {
        await handleRedirect();
        initializeSession();
        await checkAuth();
      } catch (error: any) {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsInitializing(false);
      }
    };

    initAuth0();
  }, [handleRedirect, checkAuth]);

  // Memoized actions
  const login = useCallback(async (): Promise<void> => {
    try {
      await auth0Client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: window.location.origin,
          audience: auth0Config.audience,
          scope: auth0Config.scope,
        },
      });
    } catch (err) {
      console.error("Auth0 login error:", err);
      throw err;
    }
  }, []);

  const logout = useCallback((): void => {
    auth0Client.logout({ logoutParams: { returnTo: window.location.origin } });
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const getToken = useCallback(async (): Promise<string> => {
    const token = await auth0Client.getTokenSilently({
      authorizationParams: {
        audience: auth0Config.audience,
        scope: auth0Config.scope,
      },
    });

    // Handle refresh token errors
    if (
      token === "missing_refresh_token" ||
      token.includes("Missing Refresh Token")
    ) {
      await handleRefreshTokenFailure();
    }
    return token;
  }, [handleRefreshTokenFailure]);

  const getUserEmail = useCallback((): string => {
    return user?.email || "unknown@example.com";
  }, [user]);

  // Memoized context value
  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated,
      isInitializing,
      user,
      login,
      logout,
      getToken,
      getUserEmail,
      checkTokenValidity,
    }),
    [
      isAuthenticated,
      isInitializing,
      user,
      login,
      logout,
      getToken,
      getUserEmail,
      checkTokenValidity,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  return useContext(AuthContext);
};
